import React from "react";
import {action, observable, computed, makeObservable} from "mobx";
import {CompareStore} from "./stores/compare-store-mobx";
import {LayerListStore} from "./stores/layer-list-store-mobx";
import {MapFinderStore} from "./stores/map-finder-store-mobx"
import {PopupStore} from "./stores/popup-store-mobx";
import {TopicSwitcherStore} from "./stores/topic-switcher-store-mobx";

export type StoreType = CompareStore | LayerListStore | MapFinderStore | PopupStore | TopicSwitcherStore;

export class UiState {
    @observable activeTool: string;
    @observable language: string;
    @observable menuVisible: boolean;
    @observable pendingRequestCount: number;
    @observable sidebarVisible: boolean;
    @observable sidebarHigh: boolean;
    @observable widgets = observable.map<string, React.Component>();

    /**
     * App window size
     */
    @observable uiWidth: number | undefined;
    @observable uiHeight: number | undefined;
    @observable uiOffsetTop: number | undefined;
    @observable uiOrientation: "landscape" | "portrait" | undefined;
    @observable isSmallDevice: boolean | undefined;

    /**
     * Remember selected tab per station
     */
    @observable selectedTabs = observable.map<number, number>();

    /**
     * Resizing state of a popup window
     */
    @observable resizing = observable.box(false);
    @observable toolGraphic: __esri.Graphic | null = null;
    stores: Map<string, StoreType> = new Map<string, StoreType>();

    sublayerMap = new Map<string, string>();
    constructor() {
        makeObservable(this);
        this.setActiveTool("");
        this.setLanguage("de-CH");
        this.resetPendingRequestCount();
        this.setSidebarVisible(false);
        this.setUiOffsetTop();
        this.handleWindowResizeWhenSidebarOpen = this.handleWindowResizeWhenSidebarOpen.bind(this);
    }

    @computed get appIsInSync() {
        return this.pendingRequestCount === 0;
    }
    @computed
    get isSidebarVisible() {
        return this.sidebarVisible;
    }
    get CompareStore(): CompareStore {
        if(!this.stores.has("compare")){
            this.stores.set(
                "compare",
                new CompareStore()
            );
        }
        return this.stores.get("compare") as CompareStore;
    }
    get LayerListStore(): LayerListStore {
        if(!this.stores.has("layer-list")){
            this.stores.set(
                "layer-list",
                new LayerListStore()
            );
        }
        return this.stores.get("layer-list") as LayerListStore;
    }
    get PopupStore(): PopupStore {
        if(!this.stores.has("popup")){
            this.stores.set(
                "popup",
                new PopupStore()
            );
        }
        return this.stores.get("popup") as PopupStore;
    }
    get TopicSwitcherStore(): TopicSwitcherStore {
        if(!this.stores.has("topic-switcher")){
            this.stores.set(
                "topic-switcher",
                new TopicSwitcherStore()
            );
        }
        return this.stores.get("topic-switcher") as TopicSwitcherStore;
    }
    get MapFinderStore(): MapFinderStore {
        if(!this.stores.has("map-finder")){
            this.stores.set(
                "map-finder",
                new MapFinderStore()
            );
        }
        return this.stores.get("map-finder") as MapFinderStore;
    }
    @action
    addWidget(key: string, widget: React.Component) {
        this.widgets.set(key, widget);
    }

    @action
    setActiveTool(tool: string, graphic?: __esri.Graphic | null) {
        this.setToolGraphic(graphic);
        this.activeTool = tool;
    }

    @action
    setLanguage(lang: string) {
        this.language = lang;
    }

    @action
    setUiSize(width: number | undefined, height: number | undefined, isSmallDevice?: boolean) {
        if(typeof width === "number" && typeof height === "number") {
            this.uiWidth = width;
            this.uiHeight = height;
            this.uiOrientation = height > width? "portrait": "landscape";
            this.isSmallDevice = isSmallDevice;
        }
    }
    @action
    setUiOffsetTop(uiOffsetTop?: number) {
        this.uiOffsetTop = uiOffsetTop? uiOffsetTop: 0;
    }

    @action
    resetPendingRequestCount() {
        this.pendingRequestCount = 0;
    }

    @action
    removeWidget(key: string) {
        this.widgets.delete(key);
    }

    @action
    setMenuVisible(visible: boolean) {
        this.menuVisible = visible;
    }

    @action
    setSidebarVisible(visible: boolean) {
        if (visible) {
            // add listener when menu open
            window.addEventListener("resize", this.handleWindowResizeWhenSidebarOpen);
        } else {
            window.removeEventListener("resize", this.handleWindowResizeWhenSidebarOpen);
        }
        this.sidebarVisible = visible;
    }
    @action
    setToolGraphic(toolGraphic?: __esri.Graphic | null) {
        if(typeof toolGraphic === "undefined"){
            toolGraphic = null;
        }
        this.toolGraphic = toolGraphic;
    }
    @action
    toggleSidebarVisible() {
        this.setSidebarVisible(!this.sidebarVisible);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleWindowResizeWhenSidebarOpen(event: any) {
        if (this.isSidebarVisible) {
            this.setSidebarVisible(false);
        }
        // only resize once
        window.removeEventListener("resize", this.handleWindowResizeWhenSidebarOpen);
    }
    @action
    setSidebarHigh(isHigh: boolean){
        this.sidebarHigh = isHigh;
    }
    /**
     * Start resizing
     */
    startResizing() {
        this.resizing.set(true);
    }

    /**
     * Stop resizing
     */
    stopResizing() {
        this.resizing.set(false);
    }

    isResizing(): boolean {
        return this.resizing.get();
    }

}
